//@flow
import React from 'react';
import injectSheet from 'react-jss';
import  SVGInline from 'react-svg-inline';
import type { Node } from 'react';
import { CustomHiverBadge, SessionedLink } from 'h-commons'; 
import { connect } from 'react-redux';
import crownIcon from '../images/yellowCrown.svg';
import timerIcon from '../images/timer.svg';
import ReactTooltip from 'react-tooltip';
import { theme } from '@hiver/hiver-ui-kit';
import { NewBadgeScreens } from '../constants';
import styled from 'styled-components';
import TimerIcon from './TimerIcon/TimerIcon';

const CenterSVGInline = styled(SVGInline)`
    display: flex;
    align-items: center;
`;

const hiverBadgeStyles = {
    '&.MuiChip-root .MuiChip-label': {
        padding: 0
    },
    marginTop: theme.spacing(0.75)
};

const Sidebar = (props: Props) => {
    const {
        classes, links, headerContent, root, variant,isTrialFeatureEnabled, renderLinksSection = true, additionalClass = '', hide = false
    } = props;
    
    function renderHeader() {
        return (
            <div className={classes.header}>
                {headerContent}
            </div>
        );
    }

    const hasNewBadge = (sectionName) => {
        return NewBadgeScreens.includes(sectionName);
    }

    function renderLinkItem(item, idx) {
        const path = root + item.to;
        let className = item.disabled  ? classes.disabledSideBarOption : classes.sideBarOption;
        let activeClassName = classes.selectedSidebarOption;
        if (variant === 'material') {
            className = classes.materialSidebarOption
            activeClassName = classes.materialSelectedSidebarOption
        }
        const isTrialActive = item.trialState === 'active';
        const showNewBadge = hasNewBadge(item.label);
        const { isExploringHigherPlan = false } = item
        const isFeatureTrial = isTrialFeatureEnabled && item.isTrial && !item.isPurchased

        return <SessionedLink
                key={`sidebar-link-${idx}`}
                type={"NavLink"}
                exact={item.exact}
                isActive={item.isActive}
                activeClassName={activeClassName}
                className={`${className} ${additionalClass}`}
                to={path}
                label={item.label}
                onItemClick={item.onClick}
                gainsightEvent={item.gainsightEvent}
                data-tip
                data-for={`disabledToolTip-${item.label}`}
                >
                { variant === 'material' && item.label }
                { variant !== 'material' && <div className="h-smedit--sidebar-option">
                    <div className='addonIconContainer'>
                        <span  className="h-h3 sp_bt">
                            {item.label}
                            {!item.hasUpgrade && (showNewBadge || item.showBetaTag) && (
                                <div className="h-new-badge">{item.showBetaTag ? 'Beta' : 'New'}</div>
                            )}
                    </span>
                        {item.badgeLabel && <CustomHiverBadge label={item.badgeLabel} additionalStyles={hiverBadgeStyles} />}
                        {isFeatureTrial && !isExploringHigherPlan && <SVGInline width='16px' height='16px' className="trialIcon" data-tip data-for={`trialIcon-${item.label}`} svg={isTrialActive ? timerIcon : crownIcon} />}
                        {isExploringHigherPlan && <TimerIcon />}
                        {item.hasUpgrade && <CenterSVGInline svg={crownIcon} width="16px"/>}
                    </div>
                    </div>
                }
                {isTrialActive && <ReactTooltip className='trialTooltip' id={`trialIcon-${item.label}`} place='bottom' effect='solid'>
                    <span>
                        On 14 day trial
                    </span>
                </ReactTooltip>}
                {item.disabled && item.disabledToolTip && <ReactTooltip className='trialTooltip' id={`disabledToolTip-${item.label}`} place='bottom' effect='solid'>
                    <span>{item.disabledToolTip}</span>
                </ReactTooltip>}
        </SessionedLink>
    }

    function renderLinks() {
        const linkElements = links.map(renderLinkItem);
        return (
            <div className={classes.list}>
                {linkElements}
            </div>
        )
    }

    const rootClass = variant == 'material' ? classes.materialRoot : classes.root;
    return <div className={hide ? classes.hide :rootClass}>
        {renderHeader()}
        <div className={classes.optionsContainer}>
            {renderLinksSection && renderLinks()}
        </div>
    </div>
}
type LinkData = {
    label: string,
    to: string,
    root: string
}
type Props = {
    links: Array<LinkData>,
    headerContent: Node,
    classes: Object,
    root: string,
    renderLinksSection: Boolean,
    availableFeatures: Array<Object>
}
const style = {
    root: {
        flex: 1,
        paddingRight: "15px",
        borderRight: "solid 1px", 
        borderRightColor: theme.palette.gray.gray5,
        paddingLeft: "20px",
    },
    materialRoot: {
        flex: 1,
        marginTop: "40px"
    },
    header: {
        marginTop:'11px',
        maxWidth : '149px',

        "& .h-small, .h-h2" : {
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginTop: "5px",   
        }
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around'
    },
    linkItem: {
        textDecoration: 'none',
        fontSize: 14,
        padding: '3px 5px',
        cursor: 'pointer',
        fontWeight: 300,
        borderRadius: 400
    },
    optionsWrapper: {
        minHeight: "600px",
    },
    optionsContainer: {
        marginTop: "18px",
        marginLeft: "-17px",
        "& div.h-new-badge": {
            display: "inline-block",
            background: `linear-gradient(122.53deg, ${theme.palette.yellow.primary} -16.84%, #FFC350 88.09%)`,
            color: theme.palette.gray.gray1,
            marginLeft: "13px",
            borderRadius: "14px",
            fontSize: "12px",
            fontWeight: "500",
            lineHeight: "18px",
            padding: {
                bottom: 1,
                left: 6,
                right: 6,
                top: 1
            },
        },
        "& div.addonIconContainer": {
            display: "flex",
            width:'100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            "& .sp_bt":{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
            },
            "& .trialIcon": {
                display: 'inline-flex'
            }
        }
    },
    selectedSidebarOption: {
        backgroundColor: theme.palette.gray.gray6a,
        borderRadius: "4px",
        width: "166px",
        "& span.h-h3": {
            color: `${theme.palette.gray.black} !important`,
            fontStyle: 'normal !important',
            fontWeight: '500 !important',
            fontSize: '14px !important',
            lineHeight: '24px !important',
            letterSpacing: '0.003em !important',
        }
    },
    sideBarOption: {
        textDecoration: "none",
        margin: '2px 0',
        width: "166px",
        position: 'relative',
        "&.emailSmSidebarWidth": {
            width: "190px",
        },
        "& span.h-h3": {
            color: theme.palette.gray.gray3,
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: '24px',
            letterSpacing: '0.5px',
        },
        "&:hover": {
            backgroundColor: theme.palette.gray.gray6a,
            borderRadius: "4px",
            "& span.h-h3": {
                color: theme.palette.gray.black,
              },
        },
    },
    disabledSideBarOption: {
        textDecoration: "none",
        margin: '2px 0',
        width: "166px",
        pointerEvents: 'none',
        position: 'relative',
        "& span.h-h3": {
            color: theme.palette.gray.gray3,
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: '24px',
            letterSpacing: '0.5px',
        },
    },
    materialSidebarOption: {
        textDecoration: 'none',
        margin: '2px 0',
        width: '159px',
        padding: '10px 0px 4px 10px',
        height: '22px',
        fontSize: "13px",
        color: theme.palette.gray.gray3,
        fontWeight: "400",
        '&:hover': {
            backgroundColor: theme.palette.gray.gray6a,
            borderRadius: "4px",
            color: theme.palette.gray.black,
        }
    },
    materialSelectedSidebarOption: {
        backgroundColor: theme.palette.gray.gray6a,
        color: theme.palette.gray.black,
        borderRadius: "4px",
        fontSize: "13px",
        fontWeight: "500",
    },
    hide: {
        display: 'none'
    }
}
const mapStateToProps = ({ appModes: { userSession }, userDetails}) => ({
    userSession,
    availableFeatures: userDetails.availableFeatures
});

export default connect(mapStateToProps)(injectSheet(style)(Sidebar));
